import { mkdir, writeFile } from "node:fs/promises";
import { dirname, resolve } from "node:path";
import type { IntentConfig } from "@fathom/intent";
import type { ProjectionContext, ProjectionTarget } from "./types.js";
import { generateProjections } from "./generator.js";

/**
 * Generate and write projection files to disk using async I/O.
 *
 * Files are written under `${dir}/.fathom/projections/`.
 * Creates directories as needed, overwrites existing files.
 * Collects errors for individual file writes — partial failures
 * still return successfully written paths.
 *
 * @returns List of absolute paths that were successfully written.
 * @throws If ALL writes fail (no files written at all).
 */
export async function writeProjections(
  dir: string,
  intent: IntentConfig,
  context?: ProjectionContext,
  targets?: ProjectionTarget[],
): Promise<string[]> {
  const output = generateProjections(intent, context, targets);
  const written: string[] = [];
  const errors: Array<{ path: string; error: unknown }> = [];

  for (const [relativePath, content] of output.files) {
    const absPath = resolve(dir, ".fathom", relativePath);
    try {
      await mkdir(dirname(absPath), { recursive: true });
      await writeFile(absPath, content, "utf-8");
      written.push(absPath);
    } catch (err) {
      errors.push({ path: absPath, error: err });
      console.error(`[Projections] Failed to write ${absPath}:`, err);
    }
  }

  if (errors.length > 0 && written.length === 0) {
    throw new Error(
      `Failed to write all projection files: ${errors.map((e) => e.path).join(", ")}`,
    );
  }

  return written;
}
